/**
 * `3c` — Documentos & evidências. Escopada por cliente.
 *
 * O documento é enviado **uma vez** e vinculado a N requisitos de qualquer padrão
 * (handoff v4 §1.3). Upload e download sempre por URL assinada da Lambda Gestão de
 * Documentos: upload-url → PUT direto no S3 → registro. Link externo pula o upload.
 *
 * `?requisito=<perguntaId>` (vindo do "enviar evidência" de `3b`) pré-preenche o
 * requisito no formulário.
 */
import { useState } from 'react'
import { useSearchParams } from 'react-router'

import type { Estrutura, Evidencia } from '@/api/contracts/compliance'
import {
  obterUrlDeDownload,
  useAtualizarVinculos,
  useCriarEvidencia,
  useEstrutura,
  useEvidencias,
  useExcluirEvidencia,
} from '@/api/queries/compliance'
import { tipoDoArquivo } from '@/api/upload'
import { detalhesDoErro, mensagemDoErro } from '@/components/erros'
import { EsqueletoDeFormulario, EsqueletoDeTabela } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { AreaDeTexto, Badge, Botao, Campo, Entrada } from '@/components/primitivos'
import { ZonaDeArquivo } from '@/components/ZonaDeArquivo'
import { formatarDataDeInstante, formatarTamanhoDeArquivo } from '@/format'
import { usePermission } from '@/permissions/usePermission'
import { useClienteAtual } from '@/tenant/useClienteAtual'

import { usePadraoSelecionado } from '../padrao'
import { ChipDeRequisito, SeletorDeRequisitos, type Requisito } from '../SeletorDeRequisitos'
import { useAtrasado } from '../useAtrasado'

const CHIPS_VISIVEIS = 3

const ICONE: Record<Evidencia['tipo'], string> = {
  pdf: 'file-pdf',
  docx: 'file-doc',
  link: 'link',
}

export function Documentos() {
  const idCliente = useClienteAtual()
  const { editar } = usePermission('compliance.documentos')
  const { padrao } = usePadraoSelecionado()
  const [params] = useSearchParams()
  const requisito = params.get('requisito')
  const estrutura = useEstrutura(requisito ? padrao?.id : undefined)

  const [busca, setBusca] = useState('')
  const termo = useAtrasado(busca)
  const evidencias = useEvidencias(idCliente, termo)
  const [editandoVinculos, setEditandoVinculos] = useState<Evidencia | null>(null)

  const inicial = requisito && estrutura.data ? requisitoInicial(estrutura.data, requisito) : []
  const esperandoPrefill = Boolean(requisito) && (!padrao || estrutura.isPending)

  return (
    <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="type-h4 text-text-primary">Repositório de evidências</h2>
          {evidencias.data && <Badge>{evidencias.data.length} documentos</Badge>}
          <label className="relative ml-auto">
            <span className="sr-only">Buscar documento</span>
            <i className="ph ph-magnifying-glass text-icon-muted absolute top-1/2 left-3 -translate-y-1/2" aria-hidden="true" />
            <input
              type="search"
              placeholder="buscar documento"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="rounded-control border-border-subtle bg-surface-card type-sm border py-2 pr-4 pl-9"
            />
          </label>
        </div>

        {evidencias.isPending ? (
          <EsqueletoDeTabela rotulo="Carregando evidências…" linhas={4} colunas={4} comBorda />
        ) : evidencias.isError ? (
          <EstadoDeErro erro={evidencias.error} aoTentarNovamente={() => void evidencias.refetch()} />
        ) : evidencias.data.length === 0 ? (
          <div className="border-border-subtle bg-surface-card rounded-card border">
            <EstadoVazio
              titulo={termo ? 'Nenhum documento com esse nome.' : 'Nenhuma evidência enviada ainda.'}
              descricao={termo ? undefined : 'Envie o primeiro documento pelo formulário ao lado.'}
            />
          </div>
        ) : (
          <TabelaDeEvidencias
            idCliente={idCliente}
            evidencias={evidencias.data}
            padraoAtualId={padrao?.id}
            editar={editar}
            aoEditarVinculos={setEditandoVinculos}
          />
        )}

        <p className="rounded-card border-border-subtle bg-subtle text-text-body flex gap-3 border p-4 type-sm">
          <i className="ph ph-info text-iox-navy mt-0.5" aria-hidden="true" />
          <span>
            O documento é enviado <strong>uma única vez</strong> e apontado para quantos requisitos atender, de
            qualquer padrão. Não há upload repetido do mesmo arquivo em requisitos diferentes. A relação inversa
            (quais evidências uma pergunta tem) aparece no questionário.
          </span>
        </p>
      </div>

      {editar && (
        <div className="bg-subtle rounded-card self-start p-5">
          {esperandoPrefill ? (
            <EsqueletoDeFormulario linhas={4} />
          ) : (
            <NovaEvidencia key={`${requisito ?? ''}-${inicial.length}`} idCliente={idCliente} inicial={inicial} padraoAtualId={padrao?.id} />
          )}
        </div>
      )}

      {editandoVinculos && (
        <DialogoDeVinculos
          idCliente={idCliente}
          evidencia={editandoVinculos}
          padraoAtualId={padrao?.id}
          aoFechar={() => setEditandoVinculos(null)}
        />
      )}
    </section>
  )
}

function requisitoInicial(estrutura: Estrutura, perguntaId: string): Requisito[] {
  const pergunta = estrutura.perguntas.find((p) => p.id === perguntaId)
  return pergunta
    ? [{ padraoId: estrutura.padrao.id, perguntaId: pergunta.id, ano: estrutura.padrao.ano, codigo: pergunta.codigo }]
    : []
}

function TabelaDeEvidencias({
  idCliente,
  evidencias,
  padraoAtualId,
  editar,
  aoEditarVinculos,
}: {
  idCliente: string
  evidencias: Evidencia[]
  padraoAtualId: string | undefined
  editar: boolean
  aoEditarVinculos: (e: Evidencia) => void
}) {
  const excluir = useExcluirEvidencia(idCliente)
  const [confirmando, setConfirmando] = useState<string | null>(null)
  const [erroDeAbertura, setErroDeAbertura] = useState<string | null>(null)

  async function visualizar(id: string) {
    setErroDeAbertura(null)
    try {
      window.open(await obterUrlDeDownload(idCliente, id), '_blank', 'noopener')
    } catch (erro) {
      setErroDeAbertura(mensagemDoErro(erro))
    }
  }

  return (
    <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
      {erroDeAbertura && (
        <p role="alert" className="bg-danger-100 text-danger-700 px-4 py-2 type-sm">
          {erroDeAbertura}
        </p>
      )}
      <table className="w-full text-left">
        <thead className="bg-subtle">
          <tr className="type-eyebrow text-text-muted">
            <th className="px-4 py-3">Documento</th>
            <th className="px-4 py-3">Enviado em</th>
            <th className="px-4 py-3">Requisitos vinculados</th>
            <th className="px-4 py-3">
              <span className="sr-only">Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {evidencias.map((e) => {
            const vinculos = [...e.vinculos].sort(
              (a, b) => Number(b.padraoId === padraoAtualId) - Number(a.padraoId === padraoAtualId) || b.ano - a.ano,
            )
            const excedentes = vinculos.length - CHIPS_VISIVEIS
            return (
              <tr key={e.id} className="border-border-subtle border-t align-middle">
                <td className="px-4 py-3">
                  <span className="flex items-center gap-3">
                    <i className={`ph ph-${ICONE[e.tipo]} text-iox-navy text-xl`} aria-hidden="true" />
                    <span className="flex flex-col">
                      <span className="type-label text-text-primary">{e.nome}</span>
                      <span className="type-sm text-text-muted">
                        {e.tipo === 'link'
                          ? 'link externo'
                          : e.tamanhoBytes !== null
                            ? formatarTamanhoDeArquivo(e.tamanhoBytes)
                            : e.tipo.toUpperCase()}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="text-text-body px-4 py-3 type-sm whitespace-nowrap">{formatarDataDeInstante(e.enviadaEm)}</td>
                <td className="px-4 py-3">
                  <span className="flex max-w-80 flex-wrap gap-1.5">
                    {vinculos.slice(0, CHIPS_VISIVEIS).map((v) => (
                      <ChipDeRequisito key={v.perguntaId} requisito={v} atual={v.padraoId === padraoAtualId} />
                    ))}
                    {excedentes > 0 && (
                      <span
                        title={vinculos.slice(CHIPS_VISIVEIS).map((v) => `${v.ano} · ${v.codigo}`).join(', ')}
                        className="rounded-xs bg-gray-100 text-text-muted px-2 py-1 type-sm font-semibold"
                      >
                        +{excedentes}
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {confirmando === e.id ? (
                    <span role="alertdialog" aria-label={`Excluir ${e.nome}`} className="flex flex-wrap items-center gap-2">
                      <span className="type-sm text-danger-700">excluir documento e vínculos?</span>
                      <Botao
                        variante="primaria"
                        disabled={excluir.isPending}
                        onClick={() => excluir.mutate(e.id, { onSuccess: () => setConfirmando(null) })}
                      >
                        excluir
                      </Botao>
                      <Botao onClick={() => setConfirmando(null)}>cancelar</Botao>
                      {excluir.isError && <span className="type-sm text-danger-700">{mensagemDoErro(excluir.error)}</span>}
                    </span>
                  ) : (
                    <span className="text-icon-default flex items-center gap-3">
                      <button type="button" aria-label={`visualizar ${e.nome}`} onClick={() => void visualizar(e.id)} className="hover:text-iox-teal">
                        <i className="ph ph-eye" aria-hidden="true" />
                      </button>
                      {editar && (
                        <>
                          <button type="button" aria-label={`editar vínculos de ${e.nome}`} onClick={() => aoEditarVinculos(e)} className="hover:text-iox-teal">
                            <i className="ph ph-pencil-simple" aria-hidden="true" />
                          </button>
                          <button type="button" aria-label={`excluir ${e.nome}`} onClick={() => setConfirmando(e.id)} className="hover:text-danger-700">
                            <i className="ph ph-trash" aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function NovaEvidencia({
  idCliente,
  inicial,
  padraoAtualId,
}: {
  idCliente: string
  inicial: Requisito[]
  padraoAtualId: string | undefined
}) {
  const criar = useCriarEvidencia(idCliente)
  const [modo, setModo] = useState<'arquivo' | 'link'>('arquivo')
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [recusado, setRecusado] = useState<string | null>(null)
  const [url, setUrl] = useState('')
  const [nome, setNome] = useState('')
  const [requisitos, setRequisitos] = useState<Requisito[]>(inicial)
  const [observacoes, setObservacoes] = useState('')
  const [tentou, setTentou] = useState(false)
  const [enviada, setEnviada] = useState<string | null>(null)

  const faltando = [
    ...(modo === 'arquivo' && !arquivo ? ['Arquivo'] : []),
    ...(modo === 'link' && !url.trim() ? ['Link externo'] : []),
    ...(nome.trim() ? [] : ['Nome da evidência']),
    ...(requisitos.length ? [] : ['Requisitos atendidos']),
  ]
  const camposDoServidor = (detalhesDoErro(criar.error).camposFaltantes as string[] | undefined) ?? []

  function escolher(arquivos: File[]) {
    const [primeiro] = arquivos
    if (!primeiro) return
    if (!tipoDoArquivo(primeiro)) {
      setRecusado(`${primeiro.name}: só PDF ou DOCX.`)
      return
    }
    setRecusado(null)
    setArquivo(primeiro)
    if (!nome.trim()) setNome(primeiro.name)
  }

  function enviar() {
    setTentou(true)
    setEnviada(null)
    if (faltando.length > 0) return
    const vinculos = requisitos.map((r) => ({ padraoId: r.padraoId, perguntaId: r.perguntaId }))
    const comum = { nome: nome.trim(), observacoes, vinculos }
    criar.mutate(
      modo === 'link' ? { tipo: 'link', urlExterna: url.trim(), ...comum } : { tipo: 'arquivo', arquivo: arquivo!, ...comum },
      {
        onSuccess: (evidencia) => {
          setEnviada(evidencia.nome)
          setArquivo(null)
          setUrl('')
          setNome('')
          setRequisitos([])
          setObservacoes('')
          setTentou(false)
        },
      },
    )
  }

  return (
    <form
      aria-label="Nova evidência"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        enviar()
      }}
    >
      <h2 className="type-h4 text-text-primary">Nova evidência</h2>

      <div className="flex gap-2" role="group" aria-label="Tipo de evidência">
        <Botao variante={modo === 'arquivo' ? 'accent' : 'secundaria'} aria-pressed={modo === 'arquivo'} onClick={() => setModo('arquivo')}>
          arquivo
        </Botao>
        <Botao variante={modo === 'link' ? 'accent' : 'secundaria'} aria-pressed={modo === 'link'} onClick={() => setModo('link')}>
          link externo
        </Botao>
      </div>

      {modo === 'arquivo' ? (
        <div className="flex flex-col gap-2">
          <ZonaDeArquivo
            titulo="arraste o arquivo ou selecione"
            descricao="PDF ou DOCX"
            icone="upload-simple"
            rotulo="Arquivo da evidência"
            aoEscolher={escolher}
          />
          {arquivo && (
            <span className="rounded-control bg-surface-card text-text-body inline-flex items-center gap-2 self-start px-3 py-1.5 type-sm">
              <i className="ph ph-file-text" aria-hidden="true" />
              {arquivo.name} · {formatarTamanhoDeArquivo(arquivo.size)}
              <button type="button" aria-label={`remover ${arquivo.name}`} onClick={() => setArquivo(null)}>
                <i className="ph ph-x" aria-hidden="true" />
              </button>
            </span>
          )}
          {recusado && <p className="type-sm text-danger-700">{recusado}</p>}
        </div>
      ) : (
        <Campo rotulo="Link externo" obrigatorio>
          <Entrada type="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
        </Campo>
      )}

      <Campo rotulo="Nome da evidência" obrigatorio>
        <Entrada placeholder="ex.: Política de backup em nuvem" value={nome} onChange={(e) => setNome(e.target.value)} />
      </Campo>

      <div className="flex flex-col gap-1.5">
        <span className="type-eyebrow text-text-muted">
          Requisitos atendidos <span className="text-danger-700">*</span>
        </span>
        <SeletorDeRequisitos selecionados={requisitos} aoMudar={setRequisitos} padraoAtualId={padraoAtualId} />
        <span className="type-sm text-text-muted">
          O padrão é escolhido junto com o requisito — o mesmo documento pode servir a anos diferentes.
        </span>
      </div>

      <Campo rotulo="Observações">
        <AreaDeTexto rows={4} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
      </Campo>

      {tentou && faltando.length > 0 && (
        <p role="alert" className="type-sm text-danger-700">
          Preencha: {faltando.join(', ')}.
        </p>
      )}
      {criar.isError && (
        <p role="alert" className="type-sm text-danger-700">
          {mensagemDoErro(criar.error)}
          {camposDoServidor.length > 0 && ` Campos: ${camposDoServidor.join(', ')}.`}
        </p>
      )}
      {enviada && (
        <p role="status" className="type-sm text-success-700">
          Evidência “{enviada}” enviada.
        </p>
      )}

      <Botao variante="primaria" type="submit" className="self-start" disabled={criar.isPending}>
        {criar.isPending ? 'enviando…' : 'enviar evidência'}
      </Botao>
    </form>
  )
}

function DialogoDeVinculos({
  idCliente,
  evidencia,
  padraoAtualId,
  aoFechar,
}: {
  idCliente: string
  evidencia: Evidencia
  padraoAtualId: string | undefined
  aoFechar: () => void
}) {
  const atualizar = useAtualizarVinculos(idCliente)
  const [requisitos, setRequisitos] = useState<Requisito[]>(
    evidencia.vinculos.map((v) => ({ padraoId: v.padraoId, perguntaId: v.perguntaId, ano: v.ano, codigo: v.codigo })),
  )

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Editar vínculos de ${evidencia.nome}`}
      className="bg-iox-black/40 fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) aoFechar()
      }}
    >
      <div className="bg-surface-card rounded-card shadow-panel flex w-full max-w-[560px] flex-col gap-4 p-7">
        <div className="flex items-start justify-between gap-4">
          <h2 className="type-h4 text-text-primary">Requisitos vinculados — {evidencia.nome}</h2>
          <button type="button" aria-label="Fechar" onClick={aoFechar} className="text-icon-default hover:text-iox-teal">
            <i className="ph ph-x" aria-hidden="true" />
          </button>
        </div>
        <SeletorDeRequisitos selecionados={requisitos} aoMudar={setRequisitos} padraoAtualId={padraoAtualId} />
        {requisitos.length === 0 && <p className="type-sm text-danger-700">Vincule ao menos um requisito.</p>}
        {atualizar.isError && (
          <p role="alert" className="type-sm text-danger-700">
            {mensagemDoErro(atualizar.error)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Botao onClick={aoFechar}>cancelar</Botao>
          <Botao
            variante="primaria"
            disabled={requisitos.length === 0 || atualizar.isPending}
            onClick={() =>
              atualizar.mutate(
                { id: evidencia.id, vinculos: requisitos.map((r) => ({ padraoId: r.padraoId, perguntaId: r.perguntaId })) },
                { onSuccess: aoFechar },
              )
            }
          >
            salvar vínculos
          </Botao>
        </div>
      </div>
    </div>
  )
}
